const SESSION_KEY = "uxm_analytics_session";

export type AttributionSession = {
  landingPage: string;
  entryReferrer: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  pagesVisited: string[];
  startedAt: string;
};

function readSession(): AttributionSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AttributionSession;
  } catch {
    return null;
  }
}

function writeSession(session: AttributionSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* quota / private mode */
  }
}

function parseUtm(search: string): Pick<
  AttributionSession,
  "utmSource" | "utmMedium" | "utmCampaign" | "utmTerm" | "utmContent"
> {
  const params = new URLSearchParams(search);
  const pick = (key: string) => params.get(key)?.trim() || undefined;
  return {
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
    utmTerm: pick("utm_term"),
    utmContent: pick("utm_content"),
  };
}

/** Capture first-touch attribution for this browser tab session. */
export function initAttributionSession(pathWithSearch: string): void {
  if (typeof window === "undefined") return;
  if (readSession()) return;

  const [pathname, search = ""] = pathWithSearch.split("?");
  const utm = parseUtm(search ? `?${search}` : "");

  writeSession({
    landingPage: pathname || "/",
    entryReferrer: document.referrer || "(direct)",
    ...utm,
    pagesVisited: [pathWithSearch],
    startedAt: new Date().toISOString(),
  });
}

/** Append a page to the session journey (deduped consecutive repeats). */
export function recordSessionPage(pathWithSearch: string): void {
  if (typeof window === "undefined") return;

  const existing = readSession();
  if (!existing) {
    initAttributionSession(pathWithSearch);
    return;
  }

  const last = existing.pagesVisited[existing.pagesVisited.length - 1];
  if (last === pathWithSearch) return;

  existing.pagesVisited.push(pathWithSearch);
  if (existing.pagesVisited.length > 30) {
    existing.pagesVisited = existing.pagesVisited.slice(-30);
  }
  writeSession(existing);
}

export function getAttributionSession(): AttributionSession | null {
  return readSession();
}

/** Flat params merged into GA4 events for source / journey reporting. */
export function getAttributionParams(): Record<string, string | number> {
  const session = readSession();
  if (!session) return {};

  const params: Record<string, string | number> = {
    landing_page: session.landingPage,
    entry_referrer: session.entryReferrer,
    pages_visited_count: session.pagesVisited.length,
    session_pages: session.pagesVisited.slice(-10).join(" | "),
  };

  if (session.utmSource) params.utm_source = session.utmSource;
  if (session.utmMedium) params.utm_medium = session.utmMedium;
  if (session.utmCampaign) params.utm_campaign = session.utmCampaign;
  if (session.utmTerm) params.utm_term = session.utmTerm;
  if (session.utmContent) params.utm_content = session.utmContent;

  return params;
}

export function getInternalReferrer(): string | undefined {
  if (typeof window === "undefined" || !document.referrer) return undefined;
  try {
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return undefined;
    return `${ref.pathname}${ref.search}`;
  } catch {
    return undefined;
  }
}
